import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n";

const TIMELINE_EDITORIAL = [
  { year: "1993", storyKey: "about.story.1993" },
  { year: "2005", storyKey: "about.story.growth" },
  { year: "2015", storyKey: "about.story.crisis" },
  { year: "2020", storyKey: "about.story.resilience" },
  { year: "2026", storyKey: "about.story.today" },
];

// Exact names + titles — do not change.
const TEAM = [
  {
    name: "Juan Pedro Pérez",
    role: "Founder & Managing Director",
    bio: "30+ years of livestock and land operations. Leads all on-the-ground execution at Hacienda La Esmeralda.",
    initials: "JP",
  },
  {
    name: "Manuel Pérez",
    role: "Director of Investor Relations & Business Development",
    bio: "Leads capital raising, investor engagement, and strategic business development.",
    initials: "MP",
  },
  {
    name: "Fabián Pérez",
    role: "Investor Relations – Spain",
    bio: "Supports capital raising and investor relationships across Southern Europe.",
    initials: "FP",
  },
  {
    name: "Diego Pérez",
    role: "Investor Relations – Europe",
    bio: "Manages investor communications and cross-border coordination across Europe and Venezuela.",
    initials: "DP",
  },
];

const LOGO =
  "https://customer-assets.emergentagent.com/job_a438a532-af71-4940-9c20-236f23594fbe/artifacts/duyyat7i_Logo_de_Hacienda_Esmeralda__1___1_-removebg-preview.png";

const TEAM_WATERMARK =
  "https://customer-assets.emergentagent.com/job_farm-capital/artifacts/onlrht3n_La%20Esmeralda-18.jpg";

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transitionDelay = `${delay}ms`;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return (
    <div ref={ref} className={`reveal ${className}`}>
      {children}
    </div>
  );
};

const AboutHeader = () => {
  const { t } = useT();
  return (
    <header
      data-testid="about-header"
      className="fixed top-0 left-0 right-0 z-40 backdrop-blur-sm bg-[rgba(251,249,244,0.82)] border-b border-[rgba(31,61,43,0.08)]"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-4">
        <Link
          to="/"
          data-testid="about-brand-link"
          className="flex items-center gap-3"
        >
          <img
            src={LOGO}
            alt="Esmeralda Partners"
            className="h-9 w-9 object-contain"
          />
          <div className="font-serif text-[17px] tracking-[0.01em] text-[var(--ink)] leading-tight">
            Esmeralda Partners
          </div>
        </Link>
        <nav className="flex items-center gap-6">
          <span className="hidden sm:inline text-[11px] tracking-[0.22em] uppercase text-[var(--green)] relative pb-0.5">
            {t("footer.about_link")}
            <span
              aria-hidden
              className="absolute left-0 right-0 -bottom-0.5 h-px bg-[var(--green)]"
            />
          </span>
          <Link
            to="/"
            data-testid="about-back-link"
            className="text-[11px] tracking-[0.22em] uppercase text-[var(--muted-ink)] hover:text-[var(--ink)] transition-colors inline-flex items-center gap-2"
          >
            <span aria-hidden>←</span>
            {t("about.back")}
          </Link>
        </nav>
      </div>
    </header>
  );
};

const AboutHero = () => {
  const { t } = useT();
  return (
    <section
      data-testid="about-hero"
      className="relative bg-[var(--cream)] text-[var(--ink)] pt-36 md:pt-44 pb-24 md:pb-32 px-6 md:px-12 lg:px-16"
    >
      <div className="max-w-[1100px] mx-auto">
        <div className="text-[10px] md:text-[11px] tracking-[0.34em] uppercase text-[var(--green)] mb-6 md:mb-8">
          {t("about.eyebrow")}
        </div>
        <h1
          data-testid="about-headline"
          className="font-serif text-[var(--ink)] leading-[1.05] tracking-[-0.01em] text-4xl sm:text-5xl lg:text-[64px] max-w-[18ch]"
        >
          {t("about.headline")}
        </h1>
        <p className="mt-8 font-serif italic text-[var(--muted-ink)] text-lg sm:text-xl lg:text-2xl max-w-[42ch]">
          {t("about.subtext")}
        </p>
      </div>
    </section>
  );
};

/* ─── Editorial Timeline ──────────────────────────────────────────────────── */

const TimelineContent = ({ item, t }) => (
  <div className="py-2">
    <div className="text-[10px] tracking-[0.38em] uppercase text-[var(--green)] mb-4">
      {item.year}&ensp;—&ensp;{t(`${item.storyKey}.label`)}
    </div>
    <h3 className="font-serif text-[26px] md:text-[32px] text-[var(--ink)] leading-snug tracking-[-0.01em]">
      {t(`${item.storyKey}.title`)}
    </h3>
    <p className="mt-5 text-[14px] md:text-[15px] leading-relaxed text-[var(--muted-ink)] max-w-[44ch]">
      {t(`${item.storyKey}.body`)}
    </p>
  </div>
);

const TimelineVisual = ({ year }) => (
  <div className="hidden md:flex items-center justify-center w-full h-[200px] rounded-sm bg-[var(--bone)] border border-[rgba(31,61,43,0.06)] overflow-hidden select-none">
    <span
      aria-hidden
      className="font-serif leading-none tracking-[-0.04em] text-[rgba(31,61,43,0.055)]"
      style={{ fontSize: "clamp(72px, 7vw, 100px)" }}
    >
      {year}
    </span>
  </div>
);

const AboutTimeline = () => {
  const { t } = useT();
  const lineRef = useRef(null);

  useEffect(() => {
    const el = lineRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            el.classList.add("is-revealed");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.05 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      data-testid="about-timeline"
      className="relative bg-[var(--cream)] text-[var(--ink)] border-t border-[rgba(31,61,43,0.08)] px-6 md:px-12 lg:px-16 py-28 md:py-40"
    >
      <div className="max-w-[1200px] mx-auto">
        <Reveal className="mb-20 md:mb-28">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--green)]">
            {t("about.timeline_eyebrow")}
          </div>
        </Reveal>

        <div className="relative">
          {/* Progressive vertical center line */}
          <div
            ref={lineRef}
            aria-hidden
            className="timeline-line hidden md:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-[rgba(31,61,43,0.14)]"
          />

          <ol className="relative flex flex-col">
            {TIMELINE_EDITORIAL.map((item, i) => {
              const isOdd = i % 2 === 1;
              return (
                <Reveal key={item.year} delay={i * 130} className="relative">
                  <li className="relative grid grid-cols-1 md:grid-cols-2 py-12 md:py-16">
                    {/* Center dot */}
                    <span
                      aria-hidden
                      className="hidden md:block absolute left-1/2 -translate-x-1/2 top-[52px] w-[10px] h-[10px] rounded-full bg-[var(--cream)] border-2 border-[var(--gold)] z-10 shadow-[0_0_0_5px_rgba(198,161,91,0.09)]"
                    />

                    {/* Left column */}
                    <div
                      className={`flex items-center ${
                        isOdd ? "md:order-2 md:pl-16" : "md:pr-16"
                      }`}
                    >
                      {!isOdd ? (
                        <TimelineContent item={item} t={t} />
                      ) : (
                        <TimelineVisual year={item.year} />
                      )}
                    </div>

                    {/* Right column */}
                    <div
                      className={`flex items-center ${
                        isOdd ? "md:order-1 md:pr-16" : "md:pl-16"
                      }`}
                    >
                      {isOdd ? (
                        <TimelineContent item={item} t={t} />
                      ) : (
                        <TimelineVisual year={item.year} />
                      )}
                    </div>
                  </li>

                  {/* Separator (not after last) */}
                  {i < TIMELINE_EDITORIAL.length - 1 && (
                    <div
                      aria-hidden
                      className="h-px bg-[rgba(31,61,43,0.06)] mx-0 md:mx-[50%] md:w-0"
                    />
                  )}
                </Reveal>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
};

/* ─── Team Section ────────────────────────────────────────────────────────── */

const AboutTeam = () => {
  const { t } = useT();
  return (
    <section
      data-testid="about-team"
      className="relative bg-[var(--bone)] text-[var(--ink)] border-t border-[rgba(31,61,43,0.08)] px-6 md:px-12 lg:px-16 py-28 md:py-40 overflow-hidden"
    >
      {/* Watermark — very faint background atmosphere */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${TEAM_WATERMARK})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.035,
          filter: "grayscale(100%) contrast(88%)",
        }}
      />
      {/* Bone gradient overlay to keep cards readable */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(243,239,228,0.85) 0%, rgba(243,239,228,0.72) 50%, rgba(243,239,228,0.88) 100%)",
        }}
      />

      <div className="max-w-[1280px] mx-auto relative z-10">
        <Reveal className="flex items-end justify-between flex-wrap gap-6 mb-14 md:mb-20">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--green)] mb-5">
              {t("about.team_eyebrow")}
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-[-0.005em] max-w-[20ch]">
              {t("about.team_title")}
            </h2>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {TEAM.map((m, i) => (
            <Reveal key={m.name} delay={i * 110} className="flex flex-col">
              <article
                data-testid={`team-${m.name.toLowerCase().replace(/\s+/g, "-").normalize("NFD").replace(/[̀-ͯ]/g, "")}`}
                className="team-card flex-1 border border-[rgba(31,61,43,0.1)] bg-white/40 px-6 md:px-7 py-7 md:py-9 backdrop-blur-[1px]"
              >
                <div className="flex items-center justify-center w-14 h-14 rounded-full border border-[var(--gold)] bg-[var(--cream)] text-[var(--green)] font-serif text-[15px] tracking-[0.08em] mb-7 shadow-[0_2px_10px_rgba(198,161,91,0.12)]">
                  {m.initials}
                </div>
                <h3 className="font-serif text-xl md:text-[22px] text-[var(--ink)] leading-tight tracking-[-0.005em]">
                  {m.name}
                </h3>
                <div className="mt-2 text-[10px] tracking-[0.24em] uppercase text-[var(--green)]">
                  {m.role}
                </div>
                <p className="mt-5 text-[13px] leading-relaxed text-[var(--muted-ink)]">
                  {m.bio}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Footer ──────────────────────────────────────────────────────────────── */

const AboutFooter = () => {
  const { t } = useT();
  return (
    <footer className="bg-[var(--ink)] text-[rgba(243,240,229,0.55)] border-t border-[rgba(243,240,229,0.08)]">
      <div className="max-w-[1280px] mx-auto px-6 md:px-12 lg:px-16 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 text-[12px]">
        <div className="flex items-center gap-3">
          <img
            src={LOGO}
            alt=""
            className="h-7 w-7 object-contain opacity-80"
          />
          <div className="font-serif text-[var(--cream)] text-[15px]">
            Esmeralda Partners
          </div>
        </div>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-6">
          <a
            href="mailto:esmeraldapartners@gmail.com"
            data-testid="about-footer-email-link"
            className="text-[10px] tracking-[0.04em] hover:text-[var(--gold)] transition-colors duration-200"
          >
            esmeraldapartners@gmail.com
          </a>
          <Link
            to="/"
            className="text-[10px] tracking-[0.22em] uppercase hover:text-[var(--cream)] transition-colors"
          >
            ← {t("footer.home_link")}
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default function About() {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <main data-testid="about-page" className="bg-[var(--cream)] min-h-screen">
      <AboutHeader />
      <AboutHero />
      <AboutTimeline />
      <AboutTeam />
      <AboutFooter />
    </main>
  );
}
